<template>
  <el-dialog
    :modelValue="visible"
    style="width: 55%; height: 85vh; overflow: hidden"
    center
    @close="emit('close-modal')"
  >
    <div class="dialog-content">
      <div class="header">
        <h2>Generar OP</h2>
        <el-button
          type="primary"
          :disabled="!isFormValid"
          size="default"
          style="position: absolute; right: 0"
          @click="handlePrimaryAction"
          >{{ primaryActionLabel }}</el-button
        >
      </div>
      <el-row
        :gutter="16"
        class="product-type"
      >
        <CustomInput
          v-model="selectedItems[0].tipoProducto"
          label="Tipo de Producto"
          :spanColumn="5"
          :settings="{ placeholder: 'Tipo de producto', disabled: true }"
        />
      </el-row>
      <el-row
        :gutter="16"
        class="product-type"
      >
        <el-col :span="12">
          <el-row
            :gutter="16"
            style="align-items: center"
          >
            <el-col :span="2">
              <IconCurrency style="font-size: 16px; margin-left: 4px" />
            </el-col>
            <el-col :span="22">
              <CustomInput
                v-model="selectedItems[0].monedaPedido"
                label="Moneda Pedido"
                :spanColumn="24"
                :settings="{ placeholder: 'Moneda pedido', disabled: true }"
              />
              <CustomSelect
                :label="'Moneda'"
                :spanColumn="24"
                :model-value="selectedItems[0].Moneda"
                :settings="{
                  placeholder: 'Seleccionar moneda',
                  options: monedaList,
                }"
                @update:modelValue="handleMonedaChange"
              />
            </el-col>
          </el-row>
        </el-col>
        <el-col :span="12">
          <el-row
            :gutter="16"
            style="align-items: center"
          >
            <el-col :span="2">
              <IconTapeMeasure style="font-size: 16px; margin-left: 16px" />
            </el-col>
            <el-col :span="22">
              <el-col :span="24">
                <CustomInput
                  v-model="selectedItems[0].unidadMedida"
                  label="UM Pedido"
                  :spanColumn="24"
                  :settings="{ placeholder: 'Unidad medida pedido', disabled: true }"
                />
                <CustomSelect
                  :label="'Unidad venta'"
                  :spanColumn="24"
                  :model-value="selectedItems[0].UnidadVenta"
                  :settings="{
                    placeholder: 'Seleccionar unidad',
                    options: unidadesMedida,
                  }"
                  @update:modelValue="handleUnidadVentaChange"
                />
              </el-col>
            </el-col>
          </el-row>
        </el-col>
      </el-row>
      <OrderImportTableAOp
        class="table-flex"
        @all-valid="handleAllValid"
      />
    </div>
  </el-dialog>
</template>

<script setup>
import {
  ref,
  computed,
  onBeforeMount,
  nextTick,
} from 'vue';
import { storeToRefs } from 'pinia';
import { ElMessage } from 'element-plus';
import { useRoute } from 'vue-router';
import { useOrderImportStore } from '@/views/order_import/stores/order_import_store';
import { useWorkOrderStore } from '@/views/work_orders/stores/work_order_store';
import { useMonedaStore } from '@/stores/moneda/moneda_store';
import { useUnidadMedidaStore } from '@/stores/unidad_medida/unidad_medida_store';
import OrderImportTableAOp from '@/views/order_import/components/order_import_table_a_op.vue';
import CustomInput from '@/shared/components/custom_input.vue';
import CustomSelect from '@/shared/components/custom_select.vue';
import IconCurrency from '~icons/mdi/currency-usd';
import IconTapeMeasure from '~icons/mdi/tape-measure';
import { useLoading } from '@/shared/composables/loading';

const orderImportStore = useOrderImportStore();
const { selectedItems } = storeToRefs(orderImportStore);

const workOrderStore = useWorkOrderStore();
const { ordenProduccion } = storeToRefs(workOrderStore);

const route = useRoute();

const monedaStore = useMonedaStore();
const { monedaList } = storeToRefs(monedaStore);

const unidadMedidaStore = useUnidadMedidaStore();
const { unidadesMedida } = storeToRefs(unidadMedidaStore);

const isLoading = ref(false);
const validItems = ref(false);

const props = defineProps({
  visible: { type: Boolean, default: false },
  type: { type: String, default: 'generate' },
});

const emit = defineEmits(['close-modal']);

const isFormValid = computed(() => {
  const item =
    selectedItems.value && selectedItems.value.length > 0 ? selectedItems.value[0] : null;
  if (!item) return false;

  const hasMoneda = (item.Moneda?.Codigo || 0) > 0;
  const hasUnidadVenta = (item.UnidadVenta?.Codigo || 0) > 0;
  const hasCliente = (item.Cliente?.Codigo || 0) > 0;
  const hasVendedor = (item.Vendedor?.Codigo || 0) > 0;
  const hasProducto = (item.Producto?.Codigo || 0) > 0;
  const hasCantidad = (item.cantidad || 0) > 0;

  return (
    hasMoneda &&
    hasUnidadVenta &&
    hasCliente &&
    hasVendedor &&
    hasProducto &&
    hasCantidad &&
    validItems.value
  );
});

const handleAllValid = (isValid) => {
  validItems.value = isValid;
};

const handleMonedaChange = (moneda) => {
  if (selectedItems.value.length > 0) {
    selectedItems.value.forEach((item) => {
      item.Moneda = moneda;
    });
  }
};

const handleUnidadVentaChange = (unidadVenta) => {
  if (selectedItems.value.length > 0) {
    selectedItems.value.forEach((item) => {
      item.UnidadVenta = unidadVenta;
    });
  }
};

const primaryActionLabel = computed(() => {
  if (orderImportStore.flowAction(route) === 'import') {
    return 'Importar';
  }
  return 'Generar OP';
});

const handlePrimaryAction = async () => {
  try {
    useLoading().openLoading();

    if (orderImportStore.flowAction(route) === 'import') {
      const importacionPedidoCantidadOP = selectedItems.value.map((item) => {
        const Codigo = +item?.codigoCantidad;
        return {
          CodigoImportacionPedidosCantidades: Codigo,
          cantidad: item?.cantidad,
          ImportacionPedidoCantidad: {
            Codigo,
            FechaEntrega: item.fechaEntrega,
            cantidad: item?.cantidad,
            ImportacionPedidoProducto: {
              Codigo: item.codigoProducto,
              CodigoPedido: item.codigoPedido,
              CodigoProductoPedido: item.codigoProductoPedido,
            },
          },
        };
      });

      if (importacionPedidoCantidadOP && importacionPedidoCantidadOP.length > 0) {
        if (ordenProduccion.value.ImportacionPedidoCantidadOP) {
          ordenProduccion.value.ImportacionPedidoCantidadOP = [
            ...ordenProduccion.value.ImportacionPedidoCantidadOP,
            ...importacionPedidoCantidadOP,
          ];
        } else {
          ordenProduccion.value.ImportacionPedidoCantidadOP = [];
          nextTick(() => {
            ordenProduccion.value.ImportacionPedidoCantidadOP.push(...importacionPedidoCantidadOP);
          });
        }
      }
      emit('close-modal');
      return;
    }

    await workOrderStore.createFromPedidoHTTP(selectedItems.value);
    await workOrderStore.firstRequest();
    emit('close-modal');
  } catch (error) {
    if (
      error &&
      error.response &&
      error.response.data &&
      error.response.data.errores &&
      error.response.data.errores.length > 0
    ) {
      console.log(error.response.data.errores.map((e) => e.error));
      ElMessage.error({
        message: error.response.data.errores
          .map((e) => `<span style="line-height: 1.5">${e.error}</span>`)
          .join('<br/>'),
        dangerouslyUseHTMLString: true,
        showClose: true,
        duration: 6000,
      });
    }
  } finally {
    useLoading().closeLoading();
  }
};

onBeforeMount(() => {
  monedaStore.emptyList();
  monedaStore.findAllMonedaHTTP({ page: 0, Activo: true });

  unidadMedidaStore.emptyList();
  unidadMedidaStore.findAllUnidadMedidaHTTP({
    EsUnidadVenta: true,
    page: 0,
    Activo: true,
    CodigoElementoTipo: selectedItems.value[0].codigoElementoGrupo,
  });
});
</script>

<style scoped>
.el-dialog__body {
  display: flex !important;
  flex-direction: column !important;
  height: calc(85vh - 100px) !important;
  overflow: hidden !important;
  padding: 0 !important;
}

.table-flex {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.dialog-content {
  flex: 1;
  min-height: 0;
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: 16px;
  position: relative;
}

.header {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.product-type {
  display: flex;
  justify-content: start;
  align-items: center;
  font-size: 16px;
  margin-bottom: 16px;
}
</style>
